import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  planTreeSortByOptions,
  planTreeSortDirectionOptions,
  planTreeStateFilterOptions,
  useGetPlanTreeQuery
} from '../../../../react-query/plan/query/use-get-plan-tree-query'
import {
  LoadingNextPage,
  PlanMenuTileListContainer,
  TileToolbarContainer
} from '../../styled-component'
import { planMenuItemTypeOptions, SEARCH_DEBOUNCE_MS } from '../../constants'
import { debounce, isEmpty } from 'lodash'
import { useOnInView } from 'react-intersection-observer'
import { useHistory, useParams } from 'react-router-dom'
import TileCardComponent from '../../../../components/tile-card'
import PlanMenuTileLoadList from './components/plan-menu-tile-load-list'
import PlanMenuTileToolbar from './components/plan-menu-tile-toolbar'
import PlanMenuTileBackButton from './components/plan-menu-tile-back-button'
import PlanMenuTileEmptySearch from './components/plan-menu-tile-empty-search'
import PlanMenuTileEmptyPlanTree from './components/plan-menu-tile-empty-plan-tree'
import { useGetProjectDetailsQuery } from '../../../../react-query/project/query/use-get-project-details-query'
import { SpinnerGrey } from '../../../../components/stories/atoms/icons/special'
import { iconSizeOptions } from '../../../../components/stories/atoms/icons/icons-sizes'
import { usePlanMenuStore } from '../../../../zustand/plan-menu/use-plan-menu-store'
import PlanMenuContextMenu from '../plan-menu-context-menu'
import { plansAPI } from '../../../../../proxyConfig/apiUrls'
import { useAccessSearchParam } from '../../../../zustand/authentication/use-auth-store'
import { usePlanMenuListViewControl } from '../../../../hooks/use-plan-menu-list-view-control'

const emptyUserPermissions = []
const PlanMenuTileView = () => {
  const history = useHistory()
  const { projectId } = useParams()
  const accessToken = useAccessSearchParam()
  const { viewType } = usePlanMenuListViewControl()

  const tileViewFolderHistory = usePlanMenuStore(
    (state) => state.tileViewFolderHistory
  )
  const setTileViewFolderHistory = usePlanMenuStore(
    (state) => state.setTileViewFolderHistory
  )

  const search = usePlanMenuStore((state) => state.planMenuSearch)
  const setSearch = usePlanMenuStore((state) => state.setPlanMenuSearch)
  const showInactive = usePlanMenuStore((state) => state.planMenuShowInactive)
  const setShowInactive = usePlanMenuStore(
    (state) => state.setPlanMenuShowInactive
  )

  const setPageTitle = usePlanMenuStore((state) => state.setPageTitle)

  const [selectedItems, setSelectedItems] = useState([])

  // derived states
  const currentFolderId = tileViewFolderHistory.at(-1)?.id ?? null

  const selectedItemIds = useMemo(
    () => selectedItems.map((item) => item.id),
    [selectedItems]
  )

  const stateFilter = showInactive
    ? planTreeStateFilterOptions.ALL
    : planTreeStateFilterOptions.ACTIVE

  const hasFolderHistory = tileViewFolderHistory.length > 0

  const queryParams = useMemo(
    () => ({
      stateFilter,
      search,
      sortDirection: planTreeSortDirectionOptions.DESC,
      sortBy: planTreeSortByOptions.LAST_ACTIVITY
    }),
    [stateFilter, search]
  )

  // queries
  const {
    data: planTree,
    isLoading: planTreeIsLoading,
    isFetchingNextPage: nextPageIsLoading,
    fetchNextPage,
    hasNextPage
  } = useGetPlanTreeQuery({
    projectId,
    folderId: currentFolderId,
    params: queryParams
  })

  const planList = useMemo(
    () => planTree?.pages.flatMap((p) => p.content) ?? [],
    [planTree]
  )

  const { data: projectDetails } = useGetProjectDetailsQuery({ projectId })

  const userPermissions = useMemo(
    () => projectDetails?.permissions ?? emptyUserPermissions,
    [projectDetails?.permissions]
  )

  const hasPlans = planList.length > 0
  const isSearching = !isEmpty(search)

  const isEmptySearch = isSearching && !hasPlans
  const isEmptyState = !isSearching && !hasPlans

  // handlers
  const [inputValue, setInputValue] = useState(search)

  useEffect(() => {
    setInputValue(search)
  }, [search])

  const debouncedSetSearch = useMemo(
    () => debounce(setSearch, SEARCH_DEBOUNCE_MS),
    []
  )

  const handleSearch = useCallback(
    (value) => {
      setInputValue(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch]
  )

  const handleCreatePlan = () => {
    history.push(
      `/project/${projectId}/import${currentFolderId ? `/${currentFolderId}` : ''}?view=${viewType}`
    )
  }

  const goBackFolder = () => {
    setTileViewFolderHistory(tileViewFolderHistory.slice(0, -1))
  }

  const toggleItemSelection = useCallback((item) => {
    setSelectedItems((items) => {
      const isSelected = items.some(({ id }) => id === item.id)

      return isSelected
        ? items.filter(({ id }) => id !== item.id)
        : [...items, item]
    })
  }, [])

  const openItem = (item) => {
    if (item.type === planMenuItemTypeOptions.FOLDER) {
      setTileViewFolderHistory([
        ...tileViewFolderHistory,
        { id: item.id, name: item.name }
      ])
      return
    }

    history.push(`/project/${projectId}/${item.id}/plan`)
  }

  // infinite scroll
  const onBottomReached = useCallback(() => {
    if (!hasNextPage || nextPageIsLoading) return
    fetchNextPage()
  }, [hasNextPage, nextPageIsLoading])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomRef = useOnInView(
    useCallback((inView) => {
      if (inView) onBottomReachedRef.current?.()
    }, []),
    { rootMargin: '20px', skip: !hasNextPage }
  )

  // effects
  useEffect(() => {
    const currentFolderName = tileViewFolderHistory.at(-1)?.name ?? null
    setPageTitle(currentFolderName)
  }, [tileViewFolderHistory])

  useEffect(() => {
    setSelectedItems([])
  }, [search, stateFilter, currentFolderId])

  // renders
  const handleTileClick = (item) => (event) => {
    if (event.target.closest('button')) return

    openItem(item)
  }

  const planTiles = useMemo(
    () =>
      planList.map((item) => {
        const isFolder = item.type === planMenuItemTypeOptions.FOLDER

        return (
          <TileCardComponent
            key={item.id}
            item={item}
            isFolder={isFolder}
            image={
              !isFolder
                ? `${plansAPI}/${item.id}/thumbnail.jpg${accessToken()}`
                : undefined
            }
            onClick={handleTileClick(item)}
            metaData={
              !isFolder && {
                lastActivity: item.lastActivity,
                openPins: item.openPins,
                openTasks: item.openTasks
              }
            }
            contextMenu={<PlanMenuContextMenu item={item} isTileView />}
            disabled={!item.active}
            hasCheckbox
            isSelected={selectedItemIds.includes(item.id)}
            setIsSelected={() => toggleItemSelection(item)}
            showFolderHeader
          />
        )
      }),
    [planList, selectedItemIds, toggleItemSelection, accessToken]
  )

  const planTileList = isEmptySearch ? (
    <PlanMenuTileEmptySearch />
  ) : (
    <>
      <PlanMenuTileBackButton
        hasFolderHistory={hasFolderHistory}
        goBackFolder={goBackFolder}
      />
      {isEmptyState ? (
        <PlanMenuTileEmptyPlanTree
          handleBackFolder={goBackFolder}
          handleCreatePlan={handleCreatePlan}
          isFolder={hasFolderHistory}
          userPermissions={userPermissions}
        />
      ) : (
        planTiles
      )}
    </>
  )

  return (
    <>
      <TileToolbarContainer>
        <PlanMenuTileToolbar
          setSearch={handleSearch}
          search={inputValue}
          showInactive={showInactive}
          setShowInactive={setShowInactive}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      </TileToolbarContainer>

      <PlanMenuTileListContainer>
        <PlanMenuTileLoadList isLoading={planTreeIsLoading}>
          {planTileList}
        </PlanMenuTileLoadList>
      </PlanMenuTileListContainer>
      {nextPageIsLoading && (
        <LoadingNextPage>
          <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
        </LoadingNextPage>
      )}
      <div ref={bottomRef} style={{ height: 1 }} />
    </>
  )
}

export default PlanMenuTileView
